import { DataType } from "../../../../../models/queries/queries.models";
import { EditableFieldBinding } from "../../../../components/data-grid/data-grid";
import { BaseGhgVerificationRatingFactorClientModel, GhgRatingGroups } from "../../../../../models/ghgs/ghg.models";
import { GhgRatingFactorClientModel } from "../../../../../models/vessels/vessel.models";
import { BaseGhgFactorsRenderer } from "./base-ghg-factors-renderer";

export class GhgFactorsRenderer extends BaseGhgFactorsRenderer {

    public isGeneralCargo: boolean;
    public isContainer: boolean;
    public isLngTanker: boolean;
    public isLpgTanker: boolean;
    public isBulker: boolean;
    public isRoRoCargo: boolean;
    public isCrudeProductsTanker: boolean;
    public isChemicalTanker: boolean;
    public isPassengerCruise: boolean;
    public isDirectDiesel: boolean;
    public isDieselElectric: boolean;
    public isShuttleTanker: boolean;
    public isSteamTurbine: boolean;
    public readonly highlightedProperties: string[] = [
        "Maximum DWT",
        "Number of Main Engines",
        "Main engine/steam turbine primary fuel type",
        "Number of Auxiliary Engines",
        "Auxiliary Engine Power",
        "Auxiliary Engine Primary Fuel type",
        "Auxiliary engine specific fuel consumption (Primary fuel; g/kWh)",
        "PAE (kW)"
    ]

    public get orderedFactors(): (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)[] {
        return this.allFactors;
    }

    constructor(allFactors: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)[], private additionalData: {
        vesselTypeEEDIGroupCode: string,
        isDirectDiesel: boolean,
        isDieselElectric: boolean,
        isShuttleTanker: boolean,
        isSteamTurbine: boolean
    }) {
        super(allFactors);
        const vesselTypeEEDIGroupCode = additionalData.vesselTypeEEDIGroupCode;
        this.isContainer = vesselTypeEEDIGroupCode == "CONTAINER";
        this.isGeneralCargo = vesselTypeEEDIGroupCode == "GENERAL-CARGO";
        this.isLngTanker = vesselTypeEEDIGroupCode == "LNG-TANKER";
        this.isLpgTanker = vesselTypeEEDIGroupCode == "LPG-TANKER";
        this.isBulker = vesselTypeEEDIGroupCode == "BULKER";
        this.isRoRoCargo = vesselTypeEEDIGroupCode == "RO-RO-CARGO";
        this.isCrudeProductsTanker = vesselTypeEEDIGroupCode == "CRUDE-PRODUCTS-TANKER-INC-OBO";
        this.isChemicalTanker = vesselTypeEEDIGroupCode == "CHEMICAL-TANKER";
        this.isPassengerCruise = vesselTypeEEDIGroupCode == "PASSENGER-CRUISE";
        this.isDirectDiesel = this.additionalData.isDirectDiesel;
        this.isDieselElectric = this.additionalData.isDieselElectric;
        this.isShuttleTanker = this.additionalData.isShuttleTanker;
        this.isSteamTurbine = this.additionalData.isSteamTurbine;
        this.allFactors.forEach(factor => {
            factor.isVisible = this.showFactor(factor);
        });
    }

    public isFactorReadonly(): boolean {
        return false;
    }

    public showGroup(group: string) {

        if (group == GhgRatingGroups.Gear && !this.isGeneralCargo) {
            return false;
        }
        else if (group == GhgRatingGroups.Compressors && !(this.isLngTanker && (this.isDirectDiesel || this.isDieselElectric))) {
            return false;
        }
        else if (group == GhgRatingGroups.ReliquefactionSystem && !this.isLngTanker) {
            return false;
        } else if (group == GhgRatingGroups.Capacity && !(this.isBulker || this.isChemicalTanker || this.isLngTanker || this.isLpgTanker || this.isGeneralCargo || this.isContainer)) {
            return false;
        }
        return true;
    }

    public getGroupName(group: string): string {
        return group;
    }

    public showFactor(factor: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)) {

        const gearSectionProps = ["HasCranes", "NoOfCranes", "CraneSWL", "CraneReach", "HasRoRoRamp", "RoroWeight", "HasSideloaderRamp", "SideloaderWeight"]
        const compressorsSectionProps = ["CompressorsForHighPressuredGas", "IsCOPcompComputed", "CopComp", "CompressorsForLowPressuredGas"]
        const reliquefySectionProps = ["BoilOffGasRatePerDay", "BoilOffGasToBeReliquefied", "TotalBoilOffGas", "IsCopCoolingComputed", "CopCooling"]

        let response = true;

        if (factor.propertyName == "DpClass" && !this.isShuttleTanker) {
            response = false;
        }

        if (factor.propertyName == "TwentyFootEquivalentUnitsCapacity" && !(this.isContainer || this.isGeneralCargo)) {
            response = false;
        }

        if (factor.propertyName == "MPPmotor" && !this.isDieselElectric) {
            response = false;
        }

        if (factor.propertyName == "PowerKwMax" && this.isDieselElectric) {
            response = false;
        }

        if (factor.propertyName == "SteamTurbineBoilerSfc" && !this.isSteamTurbine) {
            response = false;
        }

        if (factor.propertyName == "IsElectricPowerSuppliedByTurbineGenerator" && (!this.isSteamTurbine || !this.isLngTanker)) {
            response = false;
        }

        if (factor.propertyName == "BuiltToCSR" && !(this.isBulker || this.isChemicalTanker || this.isCrudeProductsTanker)) {
            response = false;
        }

        if (factor.propertyName == "GrossTonnage" && !(this.isPassengerCruise || this.isRoRoCargo)) {
            response = false;
        }

        if (factor.propertyName == "Displacement" && !(this.isGeneralCargo || this.isRoRoCargo)) {
            response = false;
        }

        if (factor.propertyName == "BreadthMoulded" && !(this.isGeneralCargo || this.isRoRoCargo)) {
            response = false;
        }

        if (factor.propertyName == "CargoTankCubicCapacity" && !(this.isBulker || this.isChemicalTanker || this.isLngTanker || this.isLpgTanker)) {
            response = false;
        }

        //Reliqefy Bindings
        if (reliquefySectionProps.indexOf(factor.propertyName) > -1) {
            response = this.getFactorValue("HasReliquefactionSystem") == true;

            if (response && factor.propertyName == "CopCooling") {
                response = this.getFactorValue("IsCopCoolingComputed") == true;
            }
        }

        //Gears Bindings
        if (gearSectionProps.indexOf(factor.propertyName) > -1) {

            if (response && (factor.propertyName == "NoOfCranes" ||
                factor.propertyName == "CraneSWL" ||
                factor.propertyName == "CraneReach")) {
                response = this.getFactorValue("HasCranes") == true;
            }

            if (response && (factor.propertyName == "RoroWeight")) {
                response = this.getFactorValue("HasRoRoRamp") == true;
            }

            if (response && (factor.propertyName == "SideloaderWeight")) {
                response = this.getFactorValue("HasSideloaderRamp") == true;
            }
        }

        //Compressors Bindings
        if (compressorsSectionProps.indexOf(factor.propertyName) > -1) {
            if (factor.propertyName == "CompressorsForHighPressuredGas" && !this.isDieselElectric && !this.isDirectDiesel) {
                response = false;
            }

            if (factor.propertyName == "IsCOPcompComputed" && !this.getFactorValue("CompressorsForHighPressuredGas")) {
                response = false;
            }

            if (factor.propertyName == "CopComp" && !this.getFactorValue("IsCOPcompComputed")) {
                response = false;
            }

            if (factor.propertyName == "CompressorsForLowPressuredGas" && !this.isDieselElectric && !this.isDirectDiesel) {
                response = false;
            }
        }
        return response && this.showGroup(factor.group);
    }

    public getDisplayName(factor: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)) {

        if (!factor) {
            return null;
        }
        if (factor.propertyName == "PowerKwMax") {
            if (this.isSteamTurbine) {
                return "Total steam turbine power (kW)";
            }
            return factor.displayName;
        }

        if (factor.propertyName == "MainSfc" && this.isSteamTurbine) {
            return "Steam turbine specific fuel consumption (g/kWh)";
        }

        if (factor.isNewProperty) {
            return factor.displayName + ` <sup style="text-transform: uppercase;color: #d71920">New</sup>`;
        }

        return factor.displayName;
    }

    //Checks if factor is within the HighlightedProperties array
    public isDisplayNameHighlighted(factor: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel)) {
        return this.highlightedProperties.filter(x=> this.getDisplayName(factor).toLocaleLowerCase().includes(x.toLocaleLowerCase())).length > 0;
    }
    
    public filtersHtml(factor: (BaseGhgVerificationRatingFactorClientModel | GhgRatingFactorClientModel), propValue: string, isEditable: boolean): string {

        let parameterObj = null;
        let secondParameterObj = null;
        let thirdParameterObj = "12";
        let type = factor.dataType;
        let endLabel = "";
        if (factor.propertyName.toLowerCase() == "eedi") {
            thirdParameterObj = "3";
        }

        if (factor.propertyName.toLowerCase() == "numberofmainengines" &&
            propValue == "revisedValue") {
            type = DataType.ComboBox;
            parameterObj = [
                { value: null },
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
                { value: 9 }];

            secondParameterObj = "value";
            thirdParameterObj = "value";
        }
        if (factor.propertyName.toLowerCase() == "numberofauxiliaryengines" &&
            propValue == "revisedValue") {
            type = DataType.ComboBox;
            parameterObj = [
                { value: null },
                { value: 0 },
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
                { value: 9 }];

            secondParameterObj = "value";
            thirdParameterObj = "value";
        }

        //For current values, none can be a lookup (lookup is valid only for revised and submitted)
        if (propValue == "currentValue" && type == DataType.Lookup) {
            type = DataType.Default;
        }

        const query = type == DataType.EntityFinder ? 'factor.entityFinderMappedToProperty' : 'factor.lookupMappedToProperty';
        let lookup = null;

        lookup = type == DataType.EntityFinder ? factor.entityFinderMappedToProperty : factor.lookupMappedToProperty;

        let secondParameter = "";

        //If its revised Value and RS_IceClasses then we filter by @isVisibleInGhgVerification = 1
        if (propValue == "revisedValue" && factor.lookupMappedToProperty == "RS_IceClasses") {
            secondParameter = "1";
        }

        if (["Draught", "LengthBetweenPerpendiculars"].indexOf(factor.propertyName) > -1 && factor.currentValue && propValue == "currentValue") {
            const meterToFeetMultiplier = 3.28084;
            endLabel = " (" + (meterToFeetMultiplier * factor.currentValue).toFixed(2) + " ft)";
        }

        const binding: EditableFieldBinding = {
            query: query,
            lookup: lookup,
            isEditable: isEditable,
            binding: "factor." + propValue,
            displayValueBinding: "factor." + propValue,
            value: query,
            dataType: type,
            parameter: "factor." + propValue,
            secondParameter: secondParameter,
            isNullable: true,
            buttonDelegate: propValue == "revisedValue" && isEditable ? "ghgFactorsRenderer.onRevisedValueChanged(factor)" : null,
            endLabel: endLabel,
            parameterObj: parameterObj,
            secondParameterObj: secondParameterObj,
            thirdParameterObj: thirdParameterObj,
            isValueCollection: false,
            isPropertyCollection: false
        };

        return this.getControl(binding, true);
    }
}